import { useState, useEffect } from 'react';
import {
  Webhook, Activity, Users, Plug, AlertCircle, CheckCircle2,
  XCircle, RefreshCw, ArrowRight, Plus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { webhooksApi } from '../lib/api';
import type { DashboardStats } from '../lib/api';
import { PageHeader, StatCard, Card, Button, Badge, Spinner } from './ui';

function timeAgo(ts: number): string {
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function AdminDashboard() {
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadStats = async () => {
    try {
      const response = await webhooksApi.getDashboardStats();
      if (response.success && response.data) {
        setData(response.data as unknown as DashboardStats);
      } else {
        setError(response.message || 'Failed to load dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-danger mx-auto mb-3" />
            <p className="text-ink-2 font-medium">{error || 'Failed to load dashboard'}</p>
            <button
              onClick={() => { setLoading(true); setError(null); loadStats(); }}
              className="mt-3 text-accent-ink hover:underline text-sm font-semibold"
            >
              Try again
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const { stats, webhooks, recentActivity } = data;

  const statCards = [
    { label: 'Active webhooks', value: stats.activeWebhooks, sub: `${stats.totalWebhooks} total`, icon: Webhook, tone: 'accent' as const },
    { label: 'Total leads', value: stats.totalLeads, sub: 'all time', icon: Users, tone: 'cyan' as const },
    { label: 'Leads today', value: stats.leadsToday, sub: 'last 24 hours', icon: Activity, tone: 'violet' as const },
    { label: 'CF connections', value: stats.connections, sub: 'active', icon: Plug, tone: 'amber' as const },
  ];

  return (
    <Layout>
      <div className="max-w-[1280px] mx-auto animate-fade">
        <PageHeader
          eyebrow="Overview"
          title="Dashboard"
          description="Overview of your webhook activity"
          actions={
            <>
              <Button variant="ghost" icon={RefreshCw} onClick={() => { setLoading(true); loadStats(); }}>Refresh</Button>
              <Button variant="primary" icon={Plus} onClick={() => navigate('/webhooks')}>New webhook</Button>
            </>
          }
        />

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-[18px]">
          {statCards.map((card) => (
            <StatCard
              key={card.label}
              label={card.label}
              value={card.value.toLocaleString()}
              delta={card.sub}
              icon={card.icon}
              tone={card.tone}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Webhooks */}
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <h3 className="m-0 font-display font-bold text-[15px] text-ink">Webhooks</h3>
              <button onClick={() => navigate('/webhooks')} className="inline-flex items-center gap-1 font-semibold text-[12.5px] text-accent-ink hover:underline">
                Manage <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {webhooks.length === 0 ? (
              <div className="p-8 text-center">
                <Webhook className="w-8 h-8 text-ink-3 mx-auto mb-2" />
                <p className="text-[13px] text-ink-2 font-medium">No webhooks yet</p>
                <button onClick={() => navigate('/webhooks')} className="mt-2 text-[13px] text-accent-ink hover:underline font-semibold">
                  Create your first webhook
                </button>
              </div>
            ) : (
              <div>
                {webhooks.map((wh) => (
                  <div key={wh.id} className="flex items-center justify-between px-5 py-3 border-t border-line-2 hover:bg-surface-2 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${wh.active ? 'bg-success' : 'bg-ink-3'}`} />
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-ink truncate">{wh.name}</p>
                        <p className="text-[11.5px] text-ink-3 font-medium">
                          {wh.lastReceived ? `Last: ${timeAgo(wh.lastReceived)}` : 'No data yet'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-[12.5px] text-ink-2 font-mono">{wh.totalLogs.toLocaleString()} leads</span>
                      {wh.errorCount > 0 && <Badge tone="danger">{wh.errorCount} errors</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent activity */}
          <Card className="overflow-hidden">
            <div className="px-5 py-4 border-b border-line">
              <h3 className="m-0 font-display font-bold text-[15px] text-ink">Recent activity</h3>
            </div>
            {recentActivity.length === 0 ? (
              <div className="p-8 text-center">
                <Activity className="w-8 h-8 text-ink-3 mx-auto mb-2" />
                <p className="text-[13px] text-ink-2 font-medium">No activity yet</p>
                <p className="text-[11.5px] text-ink-3 mt-1">Incoming webhook payloads will appear here</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-auto">
                {recentActivity.map((event) => (
                  <div key={event.id} className="flex items-start gap-3 px-5 py-3 border-t border-line-2">
                    {event.status === 'success'
                      ? <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                      : <XCircle className="w-4 h-4 text-danger mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[13px] font-semibold text-ink truncate">{event.contactInfo || 'Webhook received'}</p>
                        <span className="text-[11.5px] text-ink-3 flex-shrink-0">{timeAgo(event.createdAt)}</span>
                      </div>
                      <p className="text-[11.5px] text-ink-2 mt-0.5">
                        via {event.webhookName}
                        {event.status === 'error' && event.errorMessage && (
                          <span className="text-danger ml-1">— {event.errorMessage}</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
